import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { responsive } from "@/lib/media";
import { BeforeAfter } from "@/components/BeforeAfter";
import { sharePhoto } from "@/components/Lightbox";
import { MoreProjects } from "@/components/project/MoreProjects";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function Project() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    setState("loading");
    api.get(`/gallery/${id}`).then((r) => { setItem(r.data); setState("ok"); window.scrollTo(0, 0); }).catch(() => setState("missing"));
  }, [id]);

  if (state === "loading") return <div className="min-h-[50vh] grid place-items-center text-slate-400" data-testid="project-loading">Loading project…</div>;
  if (state === "missing") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="project-missing">
        <h1 className="text-2xl font-bold font-display">That project isn't here anymore.</h1>
        <Link to="/gallery" className="dg-link mt-4 inline-block">Back to the gallery →</Link>
      </div>
    );
  }
  const img = `${BASE}${item.img}`;
  const before = item.before_img ? `${BASE}${item.before_img}` : null;
  const story = (item.story || item.subtitle || "").split(/\n{2,}|\n/).filter(Boolean);
  const quoteHref = `/ask?type=install&project=${encodeURIComponent(item.title)}`;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="project-page">
      <Link to="/gallery" className="dg-link text-sm inline-flex items-center gap-1" data-testid="project-back"><ArrowLeft className="w-4 h-4" /> All projects</Link>
      <div className="mt-6 grid gap-8 lg:grid-cols-12 items-start">
        <div className="lg:col-span-7 dg-card rounded-2xl overflow-hidden dg-reveal">
          {before ? (
            <BeforeAfter before={before} after={img} alt={item.title} className="aspect-[4/3] bg-black" />
          ) : (
            <img {...responsive(img, "(max-width: 1024px) 100vw, 60vw")} alt={item.title} fetchPriority="high" decoding="async" className="w-full aspect-[4/3] object-cover" data-testid="project-image" />
          )}
        </div>
        <div className="lg:col-span-5 dg-reveal dg-reveal-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="dg-chip">{item.category}</span>
            {item.installed !== false && <span className="dg-chip text-[#FFD600] border-[#FFD600]/50">Installed by David</span>}
            {before && <span className="dg-chip">Before / after</span>}
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight font-display" data-testid="project-title">{item.title}</h1>
          <div className="mt-5 space-y-3 text-slate-300 leading-relaxed" data-testid="project-story">
            {story.map((p, i) => <p key={`${i}-${p.slice(0, 40)}`}>{p}</p>)}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to={quoteHref} className="dg-btn-primary rounded-full px-6 py-3 text-sm flex items-center justify-center gap-2" data-testid="project-quote-btn">
              Get something like this <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+13104220669" className="dg-btn-ghost rounded-full px-6 py-3 text-sm flex items-center justify-center gap-2" data-testid="project-call-btn"><Phone className="w-4 h-4" /> 310-422-0669</a>
          </div>
          <button type="button" onClick={() => sharePhoto({ id: item.id, title: item.title })} className="mt-3 min-h-[44px] px-2 -mx-2 text-sm text-slate-400 hover:text-cyan-300 inline-flex items-center gap-2 transition-colors" data-testid="project-share-btn">
            <Share2 className="w-4 h-4" /> Share this project
          </button>
          <p className="mt-6 text-xs text-slate-500">Installs are quoted for your home or shop in the LA area. Phone and video sessions are booked online — and if a problem can't be fixed remotely, David can send a technician to finish it on-site for a separate service charge.</p>
        </div>
      </div>
      <MoreProjects currentId={item.id} />
    </div>
  );
}
